import { useMemo } from "react";

import { MedicalInstitutionMemberStatus } from "../../types";
import { ReissueSecretCodeMutation } from "../../graphql/mutations";
import { Button } from "../atoms/Button";

export type ReissueSecretCodeButtonProps = {
  medicalInstitutionMemberId: string;
  medicalInstitutionMemberName: string;
  medicalInstitutionMemberStatus: MedicalInstitutionMemberStatus;
  refetchMedicalInstitutionMembers: () => void;
};

const hasDisableStatus = (status: MedicalInstitutionMemberStatus): boolean =>
  status === "停止中";

export const ReissueSecretCodeButton = ({
  medicalInstitutionMemberId,
  medicalInstitutionMemberName,
  medicalInstitutionMemberStatus,
  refetchMedicalInstitutionMembers,
}: ReissueSecretCodeButtonProps) => {
  const [reissueSecretCodeMutation] = ReissueSecretCodeMutation();

  const isDisable = useMemo(
    () => hasDisableStatus(medicalInstitutionMemberStatus),
    [medicalInstitutionMemberStatus],
  );

  const onClick = async () => {
    let secretCode: string | undefined;

    // eslint-disable-next-line no-alert
    const enableReissue: boolean = confirm(
      `「${medicalInstitutionMemberName}」さんのシークレットコードを再発行しますか？`,
    );

    if (!enableReissue) return;

    try {
      const { data: reissueSecretCodePayload, error } =
        await reissueSecretCodeMutation({
          variables: { medicalInstitutionMemberId },
        });

      if (!reissueSecretCodePayload?.reissueSecretCode) throw error;

      secretCode = reissueSecretCodePayload.reissueSecretCode.secretCode;

      if (!secretCode) throw error;
    } catch {
      // eslint-disable-next-line no-alert
      alert("シークレットコードの再発行に失敗しました");
      return;
    }

    const failedClipboardMessage = `「${medicalInstitutionMemberName}」さんのシークレットコードを再発行しました。\n閉じる前に必ずメモして下さい。\n\nシークレットコード: ${secretCode}`;
    // NOTE: confirmダイアログが表示されるとdocumentから一旦フォーカスが外れコピーできないため、documentにフォーカスが戻るのを待って処理する
    const writeTextInterval = setInterval(() => {
      if (document.hasFocus()) {
        clearInterval(writeTextInterval);
        // NOTE: documentにフォーカスがあたってからでないとチェックできない
        if (navigator.clipboard) {
          navigator.clipboard.writeText(secretCode!).then(
            () => {
              // eslint-disable-next-line no-alert
              alert(
                `「${medicalInstitutionMemberName}」さんのシークレットコードを再発行しました。\nシークレットコードはクリップボードへコピーしています。\n\nシークレットコード: ${secretCode}`,
              );
            },
            () => {
              // eslint-disable-next-line no-alert
              alert(failedClipboardMessage);
            },
          );
        } else {
          // eslint-disable-next-line no-alert
          alert(failedClipboardMessage);
        }
      }
    }, 300);

    refetchMedicalInstitutionMembers();
  };

  return (
    <Button buttonType="primary" onClick={onClick} disabled={isDisable}>
      シークレットコードの再発行
    </Button>
  );
};
